import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { newsListQueryOptions } from "@/lib/client/apex-news";
import { getNewsDisplayLabel, toNewsAssetUrlForRender } from "@/lib/utils/apex-news";
import { formatNewsUtcDate } from "@/lib/utils/date";
import { useWechatShare } from "@/hooks/use-wechat-share";
import { NEWS_LIST_PAGE_SIZE } from "@/features/news/list/constants";

type NewsListPageProps = {
  page: number;
  source: "all" | "ea" | "x";
  onPageChange: (page: number) => void;
  onSourceChange: (source: "all" | "ea" | "x") => void;
};

export function NewsListPage({ page, source, onPageChange, onSourceChange }: NewsListPageProps) {
  const offset = (page - 1) * NEWS_LIST_PAGE_SIZE;
  const { data } = useSuspenseQuery(
    newsListQueryOptions({ offset, limit: NEWS_LIST_PAGE_SIZE, source }),
  );
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / NEWS_LIST_PAGE_SIZE));
  const firstCover = data.news[0]?.coverImage
    ? toNewsAssetUrlForRender(data.news[0].coverImage)
    : undefined;

  useWechatShare({
    enabled: true,
    title: "CN Apex Club Apex 新闻中心",
    desc: "查看 Apex Legends 最新新闻、更新公告与活动动态，第一时间掌握补丁与赛事资讯。",
    imgUrl: firstCover || "/favicon.png",
  });

  return (
    <div className="space-y-4 pb-6">
      <Card className="border-red-500/20 bg-linear-to-r from-red-500/10 via-red-500/5 to-transparent">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge className="bg-red-600 text-white hover:bg-red-600">Apex News</Badge>
            <span className="text-xs text-muted-foreground">共 {data.total} 条</span>
          </div>
          <CardTitle className="text-2xl md:text-3xl font-extrabold tracking-tight">
            新闻列表
          </CardTitle>
          <CardDescription>跟进最新改动、活动和官方公告。</CardDescription>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={source === "all" ? "default" : "outline"}
              onClick={() => onSourceChange("all")}
            >
              全部
            </Button>
            <Button
              size="sm"
              variant={source === "ea" ? "default" : "outline"}
              onClick={() => onSourceChange("ea")}
            >
              EA 官方
            </Button>
            <Button
              size="sm"
              variant={source === "x" ? "default" : "outline"}
              onClick={() => onSourceChange("x")}
            >
              X Thread
            </Button>
          </div>
        </CardHeader>
      </Card>

      {data.news.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {data.news.map((item) => {
            const isTitleMissing = !item.title?.trim();
            const isTwitterSource = item.sourcePlatform === "x";
            const coverImageUrl = item.coverImage
              ? toNewsAssetUrlForRender(item.coverImage) || item.coverImage
              : null;
            const itemLabel = getNewsDisplayLabel({
              sourcePlatform: item.sourcePlatform,
              sourceAuthorName: item.sourceAuthorName,
              sourceAuthorHandle: item.sourceAuthorHandle,
              title: item.title,
              fallback: "新闻",
            });
            const sourceAuthorName = item.sourceAuthorName?.trim() || null;
            const sourceAuthorHandle = item.sourceAuthorHandle?.trim() || null;
            const sourceAuthorAvatar = item.sourceAuthorAvatar
              ? toNewsAssetUrlForRender(item.sourceAuthorAvatar) || item.sourceAuthorAvatar
              : null;

            return (
              <Link key={item.id} to="/news/$slug" params={{ slug: item.slug }}>
                <Card className="group overflow-hidden p-0 border-border/70 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
                  <div className={coverImageUrl ? "grid md:grid-cols-[240px_1fr]" : ""}>
                    {coverImageUrl ? (
                      <div className="relative h-52 w-full md:h-full">
                        <img
                          src={coverImageUrl}
                          alt={itemLabel}
                          className="size-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/35 via-black/5 to-transparent md:bg-linear-to-r" />
                      </div>
                    ) : null}

                    <CardContent className="space-y-3 p-4 md:p-5">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant={item.featured ? "default" : "secondary"}>
                          {item.featured ? "精选" : item.sourceType}
                        </Badge>
                        <Badge variant="outline">
                          {item.sourcePlatform?.toUpperCase() || "EA"}
                        </Badge>
                        {!isTwitterSource && item.sourceAuthorHandle ? (
                          <span>@{item.sourceAuthorHandle}</span>
                        ) : null}
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="size-3.5" />
                          {formatNewsUtcDate(item.publishingDate)}
                        </span>
                      </div>
                      {isTwitterSource && (sourceAuthorName || sourceAuthorHandle) ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {sourceAuthorAvatar ? (
                            <img
                              src={sourceAuthorAvatar}
                              alt={sourceAuthorName || sourceAuthorHandle || "X 用户头像"}
                              className="size-7 rounded-full border border-white/20 object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="flex size-7 items-center justify-center rounded-full border border-white/20 bg-black/20 text-[10px] font-semibold uppercase text-muted-foreground">
                              X
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                              {sourceAuthorName || sourceAuthorHandle}
                            </p>
                            {sourceAuthorHandle ? (
                              <p className="truncate text-xs text-muted-foreground">
                                @{sourceAuthorHandle}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {isTitleMissing ? (
                        <div className="space-y-1">
                          <p className="text-[11px] font-semibold tracking-[0.14em] text-cyan-700 uppercase dark:text-cyan-200">
                            X Thread 摘要
                          </p>
                          <p className="line-clamp-4 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                            {item.summary || "暂无摘要"}
                          </p>
                        </div>
                      ) : (
                        <>
                          <h2 className="line-clamp-2 text-lg font-bold leading-tight transition-colors group-hover:text-red-600">
                            {itemLabel}
                          </h2>
                          <p className="line-clamp-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                            {item.summary || "暂无摘要"}
                          </p>
                        </>
                      )}

                      <div className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-500">
                        阅读全文
                        <ChevronRight className="ml-1 size-4" />
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            暂无新闻内容
          </CardContent>
        </Card>
      )}

      {data.total > NEWS_LIST_PAGE_SIZE && (
        <div className="flex items-center justify-between rounded-xl border bg-card p-3">
          <Button
            variant="outline"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            <ChevronLeft className="size-4" />
            上一页
          </Button>

          <p className="text-sm text-muted-foreground">
            第 {page} / {totalPages} 页
          </p>

          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          >
            下一页
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
